import React, { useState, useEffect } from 'react';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { toast } from 'react-toastify';

const SubscriptionSettings = () => {
    const [settings, setSettings] = useState([]);
    const [leadTiers, setLeadTiers] = useState([]);
    const [showTierModal, setShowTierModal] = useState(false);
    const [isEditingTier, setIsEditingTier] = useState(false);
    const [newTier, setNewTier] = useState({ min_leads: 1, max_leads: '', price_per_lead: 0 });

    useEffect(() => {
        fetchSettings();
        fetchLeadTiers();
    }, []);

    const fetchLeadTiers = async () => {
        try {
            const response = await superAdminApiClient.get('/superadmin/lead-pricing-tiers');
            setLeadTiers(response.data.tiers);
        } catch (error) {
            toast.error("Failed to fetch lead pricing tiers");
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await superAdminApiClient.get('/superadmin/settings');
            setSettings(response.data.settings);
        } catch (error) {
            toast.error("Failed to fetch settings");
        }
    };

    const toggleSetting = async (key, currentValue) => {
        const newValue = currentValue === '1' ? '0' : '1';
        updateSetting(key, newValue);
    };

    const updateSetting = async (key, value) => {
        try {
            await superAdminApiClient.post('/superadmin/settings', { key, value });
            toast.success("Setting updated");
            fetchSettings();
        } catch (error) {
            toast.error("Error updating setting");
        }
    };

    const handleCreateTier = async () => {
        try {
            const payload = { ...newTier, max_leads: newTier.max_leads === '' ? null : newTier.max_leads };
            if (isEditingTier) {
                await superAdminApiClient.put(`/superadmin/lead-pricing-tiers/${newTier.id}`, payload);
                toast.success("Pricing tier updated");
            } else {
                await superAdminApiClient.post('/superadmin/lead-pricing-tiers', payload);
                toast.success("Pricing tier created");
            }
            setShowTierModal(false);
            fetchLeadTiers();
        } catch (error) {
            toast.error("Error saving tier");
        }
    };

    const openTierModal = (tier = null) => {
        if (tier) {
            setNewTier({ ...tier, max_leads: tier.max_leads || '' });
            setIsEditingTier(true);
        } else {
            setNewTier({ min_leads: 1, max_leads: '', price_per_lead: 0 });
            setIsEditingTier(false);
        }
        setShowTierModal(true);
    };

    const handleDeleteTier = async (id) => {
        if (window.confirm("Delete this pricing tier?")) {
            try {
                await superAdminApiClient.delete(`/superadmin/lead-pricing-tiers/${id}`);
                toast.success("Tier deleted");
                fetchLeadTiers();
            } catch (error) {
                toast.error("Error deleting tier");
            }
        }
    };

    const getSettingValue = (key) => {
        const setting = settings.find(s => s.key === key);
        return setting ? setting.value : '0';
    };

    return (
        <>
            <div className="mt-1">
                <div className="">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0">Subscription Settings</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-6">
                            <div className="card h-100">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Billing Configuration</h5>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="fs-14 mb-1">Pro-rated Billing</h6>
                                            <p className="text-muted mb-0">When enabled, deduct unused amount from current plan when upgrading.</p>
                                        </div>
                                        <div className="form-check form-switch form-switch-md">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="proratedBillingSwitch"
                                                checked={getSettingValue('prorated_billing') === '1'}
                                                onChange={() => toggleSetting('prorated_billing', getSettingValue('prorated_billing'))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="card h-100">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Lead Pricing Tiers</h5>
                                    <button className="btn btn-sm btn-primary" onClick={() => openTierModal()}>Add Tier</button>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Range</th>
                                                    <th>Price/Lead</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leadTiers.map(tier => (
                                                    <tr key={tier.id}>
                                                        <td>{tier.min_leads} - {tier.max_leads || '∞'} leads</td>
                                                        <td>₹{tier.price_per_lead}</td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <button className="btn btn-sm btn-link text-info p-0" onClick={() => openTierModal(tier)}>
                                                                    <i className="ri-pencil-fill"></i>
                                                                </button>
                                                                <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteTier(tier.id)}>
                                                                    <i className="ri-delete-bin-fill"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tier Modal */}
            {showTierModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0">
                            <div className="modal-header">
                                <h5 className="modal-title">{isEditingTier ? 'Edit Pricing Tier' : 'Create Lead Pricing Tier'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowTierModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-6">
                                        <div className="mb-3">
                                            <label className="form-label">Min Leads</label>
                                            <input type="number" className="form-control" value={newTier.min_leads} onChange={(e) => setNewTier({ ...newTier, min_leads: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="mb-3">
                                            <label className="form-label">Max Leads (Optional)</label>
                                            <input type="number" className="form-control" value={newTier.max_leads} onChange={(e) => setNewTier({ ...newTier, max_leads: e.target.value })} placeholder="Infinite" />
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Price Per Lead (₹)</label>
                                    <input type="number" className="form-control" value={newTier.price_per_lead} onChange={(e) => setNewTier({ ...newTier, price_per_lead: e.target.value })} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" onClick={() => setShowTierModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleCreateTier}>{isEditingTier ? 'Update' : 'Create'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SubscriptionSettings;
